import { RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { dispatchBlockedSecondsAgo } from '@/models/insuranceApplication'
import { useRequestRequote } from '@/models/requote'
import { CustomerSidebarAction } from './CustomerSidebarAction'

interface RequoteButtonProps {
  /** The customer's live application. Undefined for a customer that has never started one. */
  applicationId: string | undefined
}

/** Sends a customer's application back out to every carrier. Cai staff only. */
export function RequoteButton({ applicationId }: RequoteButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  // Set when the backend refused because Rocklake was sent moments ago.
  const [blockedSecondsAgo, setBlockedSecondsAgo] = useState<number | null>(null)
  const requote = useRequestRequote(applicationId ?? '')

  async function confirm(force = false) {
    try {
      const result = await requote.mutateAsync({ force })
      setIsOpen(false)
      toast.success('Asking carriers for new prices')
      result.requoteNotices?.forEach((notice) => toast.warning(notice))
    } catch (error) {
      const secondsAgo = dispatchBlockedSecondsAgo(error)
      if (secondsAgo !== null) {
        setIsOpen(false)
        setBlockedSecondsAgo(secondsAgo)
        return
      }
      toast.error("Couldn't request new quotes. Please try again.")
    }
  }

  return (
    <>
      <CustomerSidebarAction
        variant="outline"
        icon={<RefreshCw />}
        label="Requote"
        disabled={!applicationId}
        hint={applicationId ? undefined : 'This customer has no insurance application to re-quote'}
        onClick={() => setIsOpen(true)}
      />

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-quote this customer?</DialogTitle>
            <DialogDescription>
              The application goes back out to every carrier and the quotes on it are replaced with new ones.
              Manually added quotes, and anything already being bound, are left alone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={requote.isPending}>
              Cancel
            </Button>
            <Button onClick={() => confirm()} loading={requote.isPending ? 'Requesting…' : undefined}>
              Requote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={blockedSecondsAgo !== null}
        onOpenChange={(open) => {
          if (!open) setBlockedSecondsAgo(null)
        }}
        title="Send again?"
        description={`Rocklake was sent ${blockedSecondsAgo ?? 0} seconds ago. Re-quoting now creates another quote in the carrier's portal.`}
        confirmLabel="Send again"
        onConfirm={() => confirm(true)}
      />
    </>
  )
}
